import { queryKeys } from '@query/keys'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'

import { useAccountReadContext } from '@context/AccountContextContext'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import {
  IntroducingBrokerReferralDetailsDto,
  IntroducingBrokerReferralTypes,
} from '@model/IntroducingBrokerDetailsDto'

export interface IBReferralCodes {
  ibCode?: string
  mtCode?: string
  referralCodes: IntroducingBrokerReferralDetailsDto[]
  isLoading: boolean
  hideSelectors?: boolean
  refreshReferralCodes: () => void
  legalDocumentsSchemes: string
}

export const useIBReferralCodes = (): IBReferralCodes => {
  const { account } = useAccountReadContext()
  const introducingBrokerId = account?.clientIntroducingBroker?.id
  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()

  const queryKey = queryKeys.ibReferralCodes(introducingBrokerId)

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      const referralCodes = introducingBrokerId
        ? await apiClient.getIBReferralCodes(introducingBrokerId)
        : []

      const ibCode =
        referralCodes.find(
          ({ mainIbScheme }) => mainIbScheme.id === IntroducingBrokerReferralTypes.Default
        )?.code || undefined

      const mtCode =
        referralCodes.find(
          ({ mainIbScheme }) => mainIbScheme.id === IntroducingBrokerReferralTypes.MultiTier
        )?.code || undefined

      return {
        ibCode,
        mtCode,
        referralCodes: referralCodes || [],
      }
    },
    enabled: !!introducingBrokerId,
  })

  const legalDocumentsSchemes = useMemo(
    () =>
      data?.referralCodes
        .map((referralCode) => `&IbSchemes=${referralCode.mainIbScheme.id}`)
        .join('') || '',
    [data?.referralCodes]
  )
  const hideSelectors = useMemo(() => {
    if (!data) {
      return true
    }
    const { ibCode, mtCode } = data
    if (!ibCode && !mtCode) {
      return true
    }
    return false
  }, [data])

  return {
    ibCode: data?.ibCode,
    mtCode: data?.mtCode,
    referralCodes: data?.referralCodes || [],
    isLoading: isLoading,
    hideSelectors,
    refreshReferralCodes: () => queryClient.invalidateQueries({ queryKey }),
    legalDocumentsSchemes,
  }
}
